import JSZip from 'jszip';

/**
 * Word（.docx）导入解析。
 *
 * 目的：把用户在**其他写作软件**里写好的稿子搬进小天书。
 * 因此只需要能识别"章节标题"与"正文"，其余格式（字体/颜色/表格）一概忽略。
 *
 * 为什么不依赖 docx 库：`docx` 只负责**写**，没有解析能力；而自己读
 * `word/document.xml` 对"提取段落文本 + 判断标题层级"这个需求足够，
 * 也避免了为解析再引入一个重量级依赖。
 *
 * 章节切分策略（按优先级）：
 *   1. 段落样式为 Heading 1/2/3 或「标题 N」→ 作为章节标题
 *   2. 文本形如 `第X章/节/回 <标题>` 且不过长 → 作为章节标题
 *   3. 都没有 → 整篇作为单章，取第一段非空文字作为标题（截断）
 */

/** 段落样式 → 是否标题 */
function isHeadingStyle(style: string): boolean {
  if (!style) return false;
  const s = style.toLowerCase();
  // w:styleId / w:val 可能是 "Heading1"、"1"、"a3"（中文模板），因此同时看数值与关键词
  if (/^heading\s*[1-6]$/.test(s)) return true;
  if (/^标题\s*[1-6]$/.test(s)) return true;
  return false;
}

/**
 * 文本形如**分卷**标题：`第X卷`（也接受"卷一/卷二"这类写法）。
 *
 * 必须与章节分开识别：`第一卷 剑出云川` 里含"卷"字，
 * 若被当成章节就会得到"章节序号=第一卷、正文=剑出云川"这种错乱结果。
 */
const VOLUME_LINE_RE = /^第?\s*([0-9零一二三四五六七八九十百千万两〇]+)\s*卷[\s:：、.．-]*([^，。！？；,.!?;]{0,30})$|^卷\s*([0-9零一二三四五六七八九十百千万两〇]+)[\s:：、.．-]*([^，。！？；,.!?;]{0,30})$/;

/**
 * 文本形如**章节**标题：`第X章/节/回/篇` + 可选后半段标题。
 *
 * 两个约束避免把正文里提到"第三章"的整句话当成标题：
 *   - 整行长度必须很短（标题不会写一长句）
 *   - 序号后的标题部分**不能出现句末标点**（，。！？；），
 *     否则说明这是句子而非标题
 */
const CHAPTER_LINE_RE = /^第\s*([0-9零一二三四五六七八九十百千万两〇]+)\s*[章节回篇][\s:：、.．-]*([^，。！？；,.!?;]{0,30})$/;

/** 整行是否为分卷标题 */
export function looksLikeVolumeTitle(text: string): boolean {
  const t = text.trim();
  if (!t || t.length > 40) return false;
  return VOLUME_LINE_RE.test(t);
}

/** 整行是否为章节标题 */
export function looksLikeChapterTitle(text: string): boolean {
  const t = text.trim();
  if (!t || t.length > 40) return false;
  return CHAPTER_LINE_RE.test(t);
}

/**
 * 去掉章节标题里的序号前缀，只保留标题正文。
 *
 * 为什么需要：系统展示每章时会**自动加上**「第X章」前缀
 * （并用自己重新编号的连续序号），标题里再保留一份原文序号就会变成
 * `第一章 第一章 风雨`。
 *
 * 例：`第一章 风雨` → `风雨`；`第八章 前往问道坪` → `前往问道坪`；
 *     `第五章《东洲风华录》` → `《东洲风华录》`
 *
 * 只有序号、没有标题正文时（如 `第三章`）返回空串，
 * 由调用方兜底为「第N章」（避免出现空标题）。
 */
export function extractChapterTitleText(text: string): string {
  const t = text.trim();
  const m = CHAPTER_LINE_RE.exec(t);
  if (!m) return t;
  return m[2].replace(/^[\s:：、.．-]+/, '').replace(/[\s:：、.．-]+$/, '').trim();
}

export interface ParsedChapter {
  title: string;
  /** 纯文本正文（段落以 \n 分隔，便于服务端按段落拆成 HTML） */
  content: string;
}

export interface ParsedVolume {
  title: string;
  chapters: ParsedChapter[];
}

export interface ParsedWordDoc {
  /** 文档整体标题（取第一个标题，缺省用文件名） */
  bookTitle?: string;
  /** 识别出的分卷；没有分卷时为一个空标题的默认卷 */
  volumes: ParsedVolume[];
  /** 命中的标题层级，便于向用户解释识别结果 */
  headingLevels: number[];
  /** 未能识别出任何标题时为 true（整篇作为单章） */
  singleChapter: boolean;
}

/**
 * 从 `word/document.xml` 里按顺序取出段落的 (文本, 样式)。
 *
 * 这里**不依赖 `DOMParser`**：本仓库的 vitest 跑在 node 环境（无 DOM），
 * 用 DOMParser 会让这段逻辑完全无法单测 —— 而"章节切分对不对"恰恰最需要测试。
 * 也不用正则解析 XML（脆弱），而是写一个按标签流扫描的小工具，对这段结构够用。
 */
function decodeXmlEntities(s: string): string {
  return s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, d: string) => String.fromCodePoint(Number(d)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, h: string) => String.fromCodePoint(parseInt(h, 16)))
    // &amp; 必须最后替换，否则 "&amp;lt;" 会被二次解码
    .replace(/&amp;/g, '&');
}

/** 取某个标签的局部名（忽略可能的命名空间前缀，如 `w:t` → `t`） */
function localName(tag: string): string {
  const idx = tag.indexOf(':');
  return idx >= 0 ? tag.slice(idx + 1) : tag;
}

export function extractParagraphs(documentXml: string): Array<{ text: string; style: string }> {
  const out: Array<{ text: string; style: string }> = [];
  const tagRe = /<(\/?)([A-Za-z_][\w:.-]*)((?:"[^"]*"|'[^']*'|[^>"'])*?)(\/?)>/g;

  let inParagraph = false;
  let text = '';
  let style = '';
  let inText = false;
  let inPPr = false;
  let lastIndex = 0;
  let m: RegExpExecArray | null;

  const flushText = (raw: string) => {
    if (inText) text += decodeXmlEntities(raw);
  };

  while ((m = tagRe.exec(documentXml)) !== null) {
    // 标签之间的内容才是文本（例如 <w:t>风雨</w:t> 里的"风雨"）
    if (m.index > lastIndex) flushText(documentXml.slice(lastIndex, m.index));
    lastIndex = tagRe.lastIndex;

    const closing = m[1] === '/';
    const name = localName(m[2]);
    const attrs = m[3] ?? '';
    const selfClosing = m[4] === '/';

    if (closing) {
      switch (name) {
        case 't': inText = false; break;
        case 'pPr': inPPr = false; break;
        case 'p':
          out.push({ text: text.replace(/\u00a0/g, ' ').trim(), style });
          inParagraph = false;
          text = '';
          style = '';
          break;
        default: break;
      }
      continue;
    }

    if (selfClosing) {
      // 注意：Word 里 `<w:pStyle/>` 是**自闭合**的，属性必须在这里处理。
      // 之前把属性处理全放在下面的 switch 里，而这些标签永远进不去
      // —— 导致"按 Heading 样式识别标题"始终失效（无样式的文档看不出来）。
      if (name === 'pStyle' && inParagraph && inPPr) {
        const v = /(?:^|\s)[\w:.-]*val\s*=\s*"([^"]*)"/.exec(attrs);
        if (v) style = v[1];
      } else if (name === 'tab' && inParagraph) {
        text += '\t';
      } else if (name === 'br' && inParagraph) {
        text += '\n';
      }
      continue;
    }

    switch (name) {
      case 'p':
        inParagraph = true;
        text = '';
        style = '';
        break;
      case 'pPr':
        if (inParagraph) inPPr = true;
        break;
      case 't':
        if (inParagraph) inText = true;
        break;
      default:
        break;
    }
  }
  // 处理最后一个标签之后的残留文本
  if (lastIndex < documentXml.length) flushText(documentXml.slice(lastIndex));

  return out;
}

/** 解析 .docx 为「章节列表」 */
export async function parseWordDocument(data: ArrayBuffer): Promise<ParsedWordDoc> {
  const zip = await JSZip.loadAsync(data);
  const docEntry = zip.file('word/document.xml');
  if (!docEntry) {
    throw new Error('不是有效的 Word 文档（缺少 word/document.xml）');
  }
  const xml = await docEntry.async('string');
  const paragraphs = extractParagraphs(xml);

  // 先按结构定位书名/分卷/章节，再填充正文
  const layout: Array<
    | { kind: 'book'; text: string }
    | { kind: 'volume'; text: string }
    | { kind: 'chapter'; text: string }
    | { kind: 'body'; text: string }
  > = paragraphs
    .filter((p) => p.text)
    .map(({ text, style }) => {
      const heading = isHeadingStyle(style);
      if (looksLikeVolumeTitle(text)) return { kind: 'volume' as const, text };
      if (heading || looksLikeChapterTitle(text)) return { kind: 'chapter' as const, text };
      return { kind: 'body' as const, text };
    });

  // 记录实际遇到的标题层级（仅用于向用户说明识别结果）
  const headingLevels = paragraphs
    .filter((p) => isHeadingStyle(p.style))
    .map((p) => Number(/(\d)/.exec(p.style)?.[1]) || 1);

  const bookTitle = detectBookTitle(layout);

  // 切出「分卷 → 章节」两级结构
  const volumes: ParsedVolume[] = [];
  let currentVolume: ParsedVolume | null = null;
  let currentChapter: ParsedChapter | null = null;
  const leadingBody: string[] = [];

  const ensureVolume = (): ParsedVolume => {
    if (!currentVolume) {
      currentVolume = { title: '', chapters: [] };
      volumes.push(currentVolume);
    }
    return currentVolume;
  };

  const pushChapter = () => {
    if (currentChapter && (currentChapter.content.trim() || currentChapter.title)) {
      ensureVolume().chapters.push(currentChapter);
    }
    currentChapter = null;
  };

  for (const item of layout) {
    if (item.kind === 'book') continue; // 书名已单独取出

    if (item.kind === 'volume') {
      pushChapter();
      currentVolume = { title: item.text, chapters: [] };
      volumes.push(currentVolume);
      continue;
    }

    if (item.kind === 'chapter') {
      pushChapter();
      // 去掉原标题里的「第X章」：系统展示时会自动加前缀并用自己的连续编号，
      // 否则会出现 `第一章 第一章 风雨` 这种重复。
      // 若原标题只有序号（如"第三章"），extractChapterTitleText 返回空串，
      // 这里保留原文，载荷构建阶段会兜底为「第N章」。
      const stripped = extractChapterTitleText(item.text);
      currentChapter = { title: stripped || item.text, content: '' };
      continue;
    }

    // 正文
    if (currentChapter) {
      currentChapter.content += (currentChapter.content ? '\n' : '') + item.text;
    } else if (currentVolume) {
      // 卷标题与首个章标题之间的文字，先挂到卷下（若之后有章节会并入首章）
      currentChapter = { title: '', content: item.text };
    } else {
      leadingBody.push(item.text);
    }
  }
  pushChapter();

  // 没有识别出任何章节：整篇作为一章
  const totalChapters = volumes.reduce((n, v) => n + v.chapters.length, 0);
  if (totalChapters === 0) {
    const first = leadingBody[0];
    const title = first ? first.slice(0, 60) : (bookTitle || '导入的正文');
    const body = first === title ? leadingBody.slice(1) : leadingBody;
    return {
      bookTitle,
      volumes: [{ title: '', chapters: [{ title, content: body.join('\n') }] }],
      headingLevels,
      singleChapter: true,
    };
  }

  // 丢弃空章节（可能是"卷标题后紧跟章标题"留下的占位）
  for (const v of volumes) v.chapters = v.chapters.filter((c) => c.title || c.content.trim());

  // 书名前的正文并入第一章开头（作者常在标题前写简介）
  if (leadingBody.length > 0) {
    const firstChapter = volumes.find((v) => v.chapters.length > 0)?.chapters[0];
    if (firstChapter) {
      firstChapter.content = [...leadingBody, firstChapter.content].filter(Boolean).join('\n');
    }
  }

  const nonEmpty = volumes.filter((v) => v.chapters.length > 0);
  return {
    bookTitle,
    volumes: nonEmpty.length > 0 ? nonEmpty : [{ title: '', chapters: [] }],
    headingLevels,
    singleChapter: false,
  };
}

/**
 * 文档最开头的短行通常是书名。
 *
 * 判据：它既不是分卷也不是章节，且**紧随其后就是分卷/章节标题** ——
 * 只有满足这一点才认定为书名，避免把正文首段误当书名（那样会凭空丢一段）。
 */
function detectBookTitle(
  layout: Array<{ kind: 'book' | 'volume' | 'chapter' | 'body'; text: string }>,
): string | undefined {
  const first = layout[0];
  if (!first || first.kind !== 'body') return undefined;
  const next = layout[1];
  if (!next || (next.kind !== 'volume' && next.kind !== 'chapter')) return undefined;
  if (first.text.length > 50) return undefined;
  return first.text;
}

// ── 转成服务端 /api/books/import 可接受的载荷 ────────────────────────

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** 纯文本段落 → 编辑器可用的 HTML */
export function textToHtml(text: string): string {
  const paragraphs = text.split(/\n+/).map((l) => l.trim()).filter(Boolean);
  if (paragraphs.length === 0) return '';
  return paragraphs.map((p) => `<p>${escapeHtml(p)}</p>`).join('');
}

/**
 * 生成占位 id。
 *
 * 必须是**每个卷都不同**的合法 UUID：服务端导入用 `volume_id → volumes[].id`
 * 的映射来还原章节归属（见 routes/io.ts 的 volumeMap），
 * 若所有卷共用一个 id，全部章节都会落到第一个卷里。
 */
function placeholderUuid(n: number): string {
  return `00000000-0000-4000-8000-${String(n).padStart(12, '0')}`;
}

export interface WordImportOptions {
  /** 书架已有标题（用于避免重名） */
  existingTitles?: string[];
  /** 导入时字数按纯文本字符计 */
  now?: string;
}

/**
 * 组装导入载荷：一个作品 + 若干分卷 + 扁平章节列表。
 *
 * 形状与 `.xts` 备份保持一致（`workInfo` / `volumes` / `chapters` 三个顶层数组，
 * 章节通过 `volume_id` 指向卷的 `id`），这样服务端导入逻辑无需为 Word 开分支。
 */
export function buildWordImportPayload(
  bookTitle: string,
  volumes: ParsedVolume[],
  options: WordImportOptions = {},
): Record<string, unknown> {
  const now = options.now ?? new Date().toISOString();

  // 重名时加后缀，避免书架里出现两个同名作品（与批量导入的处理一致）
  let title = bookTitle;
  const existing = new Set(options.existingTitles ?? []);
  if (existing.has(title)) title = `${title}_副本`;

  const bookId = placeholderUuid(1);
  // 章节需要在全作品范围内连续编号
  let chapterNumber = 0;

  const volumeRows: Array<Record<string, unknown>> = [];
  const chapterRows: Array<Record<string, unknown>> = [];

  volumes.forEach((v, vi) => {
    const volumeId = placeholderUuid(vi + 10);
    volumeRows.push({
      id: volumeId,
      book_id: bookId,
      title: v.title || `第${vi + 1}卷`,
      description: '由 Word 文档导入',
      order_index: vi + 1,
      created_at: now,
      updated_at: now,
    });

    for (const c of v.chapters) {
      chapterNumber += 1;
      const content = textToHtml(c.content);
      // 再剥一次「第X章」：既覆盖"只有序号"的裸标题（此时兜底为第N章），
      // 也保证无论字段从哪条路径来，落库标题都不带序号前缀。
      const strippedTitle = extractChapterTitleText(c.title);
      chapterRows.push({
        id: placeholderUuid(1000 + chapterNumber),
        book_id: bookId,
        volume_id: volumeId,
        title: strippedTitle || `第${chapterNumber}章`,
        summary: '',
        content,
        // 纯文本字数（与服务端的字数口径一致：去标签后计字符）
        word_count: c.content.replace(/\s/g, '').length,
        chapter_number: chapterNumber,
        status: 'draft',
        target_words: 0,
        created_at: now,
        updated_at: now,
      });
    }
  });

  return {
    version: '3.0',
    type: 'xiaotianshu_export',
    timestamp: now,
    // Word 没有这些数据，明确留空，导入报告会如实显示为 0
    settings: [],
    factions: [],
    characters: [],
    items: [],
    outline: null,
    workInfo: {
      id: bookId,
      title,
      description: '',
      cover: '',
      created_at: now,
      updated_at: now,
      is_active: true,
    },
    volumes: volumeRows,
    chapters: chapterRows,
  };
}
